import { ReactComponent as PennyIcon } from "@assets/icons/penny_icon.svg";
import { ReactComponent as ZurichVerticalIcon } from "@assets/icons/zurich_vertical.svg";
import BarStepsProgress from "@components/Progress/BarStepsProgress";
import { DocumentData } from "firebase/firestore";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";

type DashboardPensionAppliedProps = {
  data: DocumentData;
};

const DashboardPensionApplied: React.FC<DashboardPensionAppliedProps> = ({
  data,
}) => {
  console.log('DashboardPensionApplied rendering with data:', {
    pension_applied: data?.pension_applied,
    retirement_value: data?.retirement_income_target_value,
    contribution_value: data?.pension_contribution_value
  });

  const pensionsViewVariants = {
    zurich_standard_prsa: {
      title: "Zurich Standard PRSA",
    },
    zurich_nonstandard_prsa: {
      title: "Zurich Non-Standard PRSA",
    },
  };

  // Validate pension_applied value
  if (!data?.pension_applied || !(data.pension_applied in pensionsViewVariants)) {
    console.error('Invalid pension_applied value:', data?.pension_applied);
    return null;
  }

  const valuesPension = useMemo(() => {
    console.log('Calculating pension values with data:', data);
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return {
      retirement_value: formatter.format(
        Number(data.retirement_income_target_value) || 0
      ),
      pension_contribution_value: formatter.format(
        Number(data.pension_contribution_value) || 0
      ),
    };
  }, [data]);

  return (
    <div className="px-4 pb-6 lg:px-[80px] md:py-10 bg-pn_paper-3 flex flex-col gap-y-0 md:gap-y-6">
      <div className="p-4 md:p-0">
        <BarStepsProgress activeStep={3} />
      </div>

      <div className="p-6 flex flex-col gap-y-6 border border-pn_gray-200 rounded-xl bg-pn_white">
        <div className="flex items-center justify-between">
          <div className="flex flex-col items-start lg:items-center gap-y-6 gap-x-6 lg:flex-row">
            <div className="bg-[#F4E34E] w-[60px] h-[60px] flex items-center justify-center rounded-full">
              <PennyIcon className="w-[24px] h-[27px]" />
            </div>
            <div className="flex flex-col gap-y-1">
              <span className="text-pn-body-large-emphasis">
                Your Penny pension is 1 step away
              </span>
              <span className="text-pn-body-small-regular text-pn_gray-600">
                Based on your details, this is your best matching pension.
                Please complete application
              </span>
            </div>
          </div>

          <Link
            to={"/pension-application"}
            className="btn-primary-sm-40 hidden lg:block"
          >
            Complete Application
          </Link>
        </div>

        <div className="border border-pn_gray-200 flex gap-x-4 items-center rounded-lg p-4">
          <div className="border w-[60px] h-[60px] lg:w-10 lg:h-10 border-pn_gray-200 bg-pn_white rounded-full flex items-center justify-center">
            <ZurichVerticalIcon className="w-[41px] h-[26px] lg:w-[27px] lg:h-[17px]" />
          </div>

          <div className="flex flex-col lg:flex-row items-start gap-y-2 lg:gap-y-0 lg:items-center flex-1">
            <span className="text-pn-body-large-emphasis flex-1 text-pn_gray-950">
              {
                pensionsViewVariants[
                  data?.pension_applied as keyof typeof pensionsViewVariants
                ].title
              }
            </span>

            <div className="flex lg:items-end flex-col gap-y-2 flex-1">
              <div className="flex items-start lg:items-center gap-x-[6px]">
                <span className="text-pn-body-small-regular text-pn_gray-950">
                  Yearly retirement income target
                </span>
                <span className="bg-pn_yellow-200 text-pn-body-small-regular text-pn_gray-950">
                  {valuesPension.retirement_value}
                </span>
              </div>

              <div className="flex items-start lg:items-center gap-x-[6px]">
                <span className="text-pn-body-small-regular text-pn_gray-950">
                  Monthly pension contribution
                </span>
                <span className="bg-pn_yellow-200 text-pn-body-small-regular text-pn_gray-950">
                  {valuesPension.pension_contribution_value}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Link
          to={"/pension-application"}
          className="btn-primary-sm-40 block lg:hidden"
        >
          Complete Application
        </Link>
      </div>

      {/* <div className="px-4 pb-4">
        <div className="border border-pn_gray-200 bg-pn_white rounded-2xl p-6 flex flex-col gap-y-[32px]">
          <div className="flex flex-col gap-y-4">
            <div className="flex items-center gap-x-6">
              <div className="rounded-full border-pn_gray-200 border bg-pn_white w-[64px] h-[64px] flex items-center justify-center">
                <ZurichVerticalIcon />
              </div>
              <span className="text-pn-headline-emphasis text-pn_gray-950">
                {
                  pensionsViewVariants[
                    data?.pension_applied as keyof typeof pensionsViewVariants
                  ].title
                }
              </span>
            </div>

            <div className="divider-dotted"></div>

            <span className="text-pn-callout-regular text-pn_gray-600">
              Based on the answers you gave us, we've made some pension
              recommendations to match your needs.
            </span>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-y-4">
                <span className="text-pn-caption-medium bg-[#F4E34E] w-fit uppercase">
                  Ret. Income Target
                </span>
                <div className="flex flex-col">
                  <span className="text-pn-large-title">
                    {valuesPension.retirement_value}
                  </span>
                  <span className="text-pn-callout-regular text-pn_gray-600">
                    per year
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-y-4">
                <span className="text-pn-caption-medium bg-[#F4E34E] w-fit uppercase">
                  Pension Contribution
                </span>
                <div className="flex flex-col">
                  <span className="text-pn-large-title">
                    {valuesPension.pension_contribution_value}
                  </span>
                  <span className="text-pn-callout-regular text-pn_gray-600">
                    per month
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Link
            to={"/pension-application"}
            className="btn-primary-lg text-center"
          >
            Complete Application
          </Link>
        </div>
      </div> */}
    </div>
  );
};
export default DashboardPensionApplied;
